import { ArrowLeft, CheckCircleFill, FileEarmarkPdf, X } from "react-bootstrap-icons";
import { FormEvent, KeyboardEvent, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import classNames from "classnames";
import Footer from "../../components/Footer/Footer";
import SiteNav from "../../components/SiteNav/SiteNav";
import Seo from "../../components/Seo/Seo";
import { useLocale } from "../../contexts/LocaleContext";
import { createHeroParticles } from "../../utils/heroParticles";
import shell from "./Career.module.scss";
import styles from "./Vacancy.module.scss";

const vacancyUrl = (id: string) => `https://api.stafflow.by/job-vacancy-public/${encodeURIComponent(id)}/json`;
const applicationUrl = (id: string) => `https://api.stafflow.by/job-vacancy-public/application/${encodeURIComponent(id)}`;

const MAX_FILE_SIZE = 25 * 1024 * 1024;

const COPY = {
  ru: {
    back: "Все вакансии",
    apply: "Откликнуться",
    applyTitle: "Отклик на вакансию",
    applyBody: "Оставьте контакты — мы свяжемся с вами после рассмотрения отклика.",
    submit: "Отправить отклик",
    sending: "Отправляем…",
    successTitle: "Отклик отправлен",
    successBody: "Спасибо! Мы изучим ваш отклик и свяжемся с вами.",
    submitError: "Не удалось отправить отклик. Попробуйте ещё раз.",
    required: "Обязательное поле",
    consentBefore: "Откликаясь на вакансию, вы даете согласие на обработку ваших персональных данных и соглашаетесь с ",
    consentLink: "политикой конфиденциальности",
    consentAfter: " CatLogic",
    consentRequired: "Необходимо ваше согласие",
    fileTooBig: "Файл больше 25 МБ",
    fileWrongType: "Нужен файл в формате PDF",
    chooseFile: "Выберите файл",
    tagsPlaceholder: "Вставьте ссылку и нажмите Enter",
    salary: "Зарплата",
    vacancyLink: "Ссылка на вакансию в службе занятости",
    loadError: "Не удалось загрузить вакансию. Возможно, она уже закрыта.",
  },
  en: {
    back: "All vacancies",
    apply: "Apply",
    applyTitle: "Apply for this role",
    applyBody: "Leave your contacts and we'll get back to you after reviewing your application.",
    submit: "Send application",
    sending: "Sending…",
    successTitle: "Application sent",
    successBody: "Thank you! We'll review your application and get in touch.",
    submitError: "Couldn't send the application. Please try again.",
    required: "Required field",
    consentBefore: "By applying, you consent to the processing of your personal data and agree to the CatLogic ",
    consentLink: "privacy policy",
    consentAfter: "",
    consentRequired: "Your consent is required",
    fileTooBig: "File is larger than 25 MB",
    fileWrongType: "Please attach a PDF file",
    chooseFile: "Choose a file",
    tagsPlaceholder: "Paste a link and press Enter",
    salary: "Salary",
    vacancyLink: "Vacancy on the employment service",
    loadError: "Couldn't load this vacancy. It may already be closed.",
  },
};

// The vacancy texts come from the Stafflow API as HTML. Strip anything executable
// before rendering, and mark "• ..." paragraphs so they can be styled as list items.
const sanitizeHtml = (html: string) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.body.querySelectorAll("script, style, iframe, object, embed, form, link, meta").forEach((node) => node.remove());
  doc.body.querySelectorAll("*").forEach((node) => {
    Array.from(node.attributes).forEach((attr) => {
      const isHandler = attr.name.startsWith("on");
      const isScriptUrl = /^\s*javascript:/i.test(attr.value);
      if (isHandler || isScriptUrl || attr.name === "style") node.removeAttribute(attr.name);
    });
  });
  doc.body.querySelectorAll("p").forEach((p) => {
    const first = p.firstChild;
    if (first?.nodeType === Node.TEXT_NODE && /^\s*[•·-]\s*/.test(first.textContent ?? "")) {
      first.textContent = (first.textContent ?? "").replace(/^\s*[•·-]\s*/, "");
      p.setAttribute("data-bullet", "");
    }
  });
  return doc.body.innerHTML;
};

// Stafflow sends "-" for fields that weren't filled in.
const isFilled = (value?: string) => !!value && value.trim() !== "" && value.trim() !== "-";

const hasText = (html?: string) => !!html && html.replace(/<[^>]*>/g, "").trim().length > 0;

// Stafflow expects the full data URL, e.g. "data:application/pdf;base64,JVBERi0x..."
const readAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

type FieldValue = string | string[] | File | undefined;

export default function VacancyView() {
  const { id = "" } = useParams();
  const { locale } = useLocale();
  const copy = COPY[locale];
  const heroParticles = useMemo(() => createHeroParticles(), []);
  const [data, setData] = useState<IVacancyResponse>();
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const findOne = async () => {
      try {
        const { data } = await axios.get<IVacancyResponse>(vacancyUrl(id));
        setData(data);
      } catch {
        setLoadFailed(true);
      }
    };

    findOne();
  }, [id]);

  const vacancy = data?.vacancy;

  const sections = useMemo(() => {
    if (!vacancy) return [];
    return [
      { label: vacancy.descriptionLabel, html: vacancy.description },
      { label: vacancy.responsibilitiesLabel, html: vacancy.responsibilities },
      { label: vacancy.requirementsLabel, html: vacancy.requirements },
      { label: vacancy.termsLabel, html: vacancy.terms },
      { label: vacancy.weOfferLabel, html: vacancy.weOffer },
    ]
      .filter((section) => hasText(section.html))
      .map((section) => ({ ...section, html: sanitizeHtml(section.html) }));
  }, [vacancy]);

  const tags = vacancy ? [vacancy.location, vacancy.workFormat, vacancy.workRegime, vacancy.employmentType].filter(isFilled) : [];
  const facts = vacancy
    ? [
        { label: copy.salary, value: vacancy.salary },
      ].filter((fact) => isFilled(fact.value))
    : [];

  const scrollToForm = () => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <main className={shell.page}>
      <Seo
        title={vacancy ? `${vacancy.name} | CatLogic` : "CatLogic | Вакансия"}
        description={vacancy ? `Вакансия «${vacancy.name}» в CatLogic. ${tags.join(", ")}` : "Вакансия в CatLogic"}
        path={`/career/${encodeURIComponent(id)}`}
      />
      <SiteNav solidThreshold={320} />

      <section className={shell.hero}>
        <div className={shell.heroParticles} aria-hidden="true">
          {heroParticles.map((particle, index) => (
            <span
              key={index}
              className={shell.particle}
              style={
                {
                  width: particle.size,
                  height: particle.size,
                  top: particle.top,
                  left: particle.left,
                  background: `radial-gradient(circle, ${particle.color}, transparent 70%)`,
                  animationDuration: particle.duration,
                  animationDelay: particle.delay,
                  "--particle-scale-mid": particle.scaleMid,
                  "--particle-scale-low": particle.scaleLow,
                } as React.CSSProperties
              }
            />
          ))}
        </div>

        <div className={shell.container}>
          <div className={styles.heroContent}>
            <Link to="/career" className={styles.backLink}>
              <ArrowLeft />
              {copy.back}
            </Link>

            {vacancy ? (
              <>
                <h1 className={styles.heroTitle}>{vacancy.name}</h1>
                {tags.length > 0 && (
                  <ul className={styles.heroTags}>
                    {tags.map((tag) => (
                      <li key={tag}>{tag}</li>
                    ))}
                  </ul>
                )}
                {data.apply && (
                  <button type="button" className={styles.heroButton} onClick={scrollToForm}>
                    {copy.apply}
                  </button>
                )}
              </>
            ) : (
              !loadFailed && (
                <div className={styles.heroSkeleton} aria-busy="true">
                  <span />
                  <span />
                </div>
              )
            )}
          </div>
        </div>
      </section>

      <div className={shell.surface}>
        <section className={shell.lightBand}>
          <div className={shell.container}>
            {loadFailed ? (
              <p className={shell.state}>{copy.loadError}</p>
            ) : !data || !vacancy ? (
              <div className={styles.contentSkeleton}>
                {Array.from({ length: 5 }, (_, index) => (
                  <span key={index} />
                ))}
              </div>
            ) : (
              <div className={styles.layout}>
                <div className={styles.content}>
                  {facts.length > 0 && (
                    <dl className={styles.facts}>
                      {facts.map((fact) => (
                        <div key={fact.label}>
                          <dt>{fact.label}</dt>
                          <dd>{fact.value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}

                  {sections.length > 0 && (
                    <div className={styles.sections}>
                      {sections.map((section) => (
                        <article key={section.label} className={styles.section}>
                          <h2>{section.label}</h2>
                          <div className={styles.richText} dangerouslySetInnerHTML={{ __html: section.html }} />
                        </article>
                      ))}
                    </div>
                  )}

                  {/* {vacancy.vacancyLink && (
                    <a className={styles.externalLink} href={vacancy.vacancyLink} target="_blank" rel="noopener noreferrer">
                      {copy.vacancyLink}
                      <BoxArrowUpRight />
                    </a>
                  )} */}
                </div>

                {data.apply && (
                  <aside id="apply" className={styles.aside}>
                    <ApplyForm vacancyId={id} apply={data.apply} copy={copy} />
                  </aside>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </main>
  );
}

function ApplyForm({ vacancyId, apply, copy }: { vacancyId: string; apply: IApply; copy: (typeof COPY)["ru"] }) {
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);

  const setValue = (name: string, value: FieldValue) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next: Record<string, string> = {};
    apply.form.forEach((field) => {
      const value = values[field.name];
      const isEmpty = value === undefined || (typeof value === "string" ? !value.trim() : Array.isArray(value) && !value.length);
      if (field.required && isEmpty) next[field.name] = copy.required;
      if (value instanceof File) {
        if (value.size > MAX_FILE_SIZE) next[field.name] = copy.fileTooBig;
        else if (value.type !== "application/pdf") next[field.name] = copy.fileWrongType;
      }
    });
    setErrors(next);
    setConsentError(!consent);
    return Object.keys(next).length === 0 && consent;
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    setStatus("sending");
    try {
      const payload: Record<string, string | string[]> = {};
      for (const field of apply.form) {
        const value = values[field.name];
        if (value === undefined) continue;
        payload[field.name] = value instanceof File ? await readAsDataUrl(value) : typeof value === "string" ? value.trim() : value;
      }
      await axios.post(applicationUrl(vacancyId), payload);
      setStatus("sent");
    } catch {
      setStatus("failed");
    }
  };

  if (status === "sent") {
    return (
      <div className={classNames(styles.formCard, styles.success)}>
        <CheckCircleFill className={styles.successIcon} />
        <h3>{copy.successTitle}</h3>
        <p>{copy.successBody}</p>
      </div>
    );
  }

  return (
    <form className={styles.formCard} onSubmit={onSubmit} noValidate>
      <h3>{copy.applyTitle}</h3>
      <p className={styles.formIntro}>{copy.applyBody}</p>

      <div className={styles.fields}>
        {apply.form.map((field) => (
        <div key={field.name} className={classNames(styles.field, { [styles.fieldWide]: WIDE_FIELD_TYPES.includes(field.type) })}>
          <label htmlFor={field.name}>
            {field.label}
            {field.required && <span className={styles.requiredMark}>*</span>}
          </label>
          <FormControl field={field} value={values[field.name]} invalid={!!errors[field.name]} copy={copy} onChange={(value) => setValue(field.name, value)} />
          {errors[field.name] ? <span className={styles.fieldError}>{errors[field.name]}</span> : field.hint && <span className={styles.fieldHint}>{field.hint}</span>}
        </div>
        ))}
      </div>

      <div className={styles.consent}>
        <label className={classNames(styles.consentLabel, { [styles.consentInvalid]: consentError })}>
          <input
            type="checkbox"
            checked={consent}
            onChange={(event) => {
              setConsent(event.target.checked);
              setConsentError(false);
            }}
          />
          <span>
            {copy.consentBefore}
            <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">
              {copy.consentLink}
            </Link>
            {copy.consentAfter}
          </span>
        </label>
        {consentError && <span className={styles.fieldError}>{copy.consentRequired}</span>}
      </div>

      {status === "failed" && <p className={styles.submitError}>{copy.submitError}</p>}

      <button type="submit" className={styles.submitButton} disabled={status === "sending"}>
        {status === "sending" ? copy.sending : copy.submit}
      </button>
    </form>
  );
}

type FormControlProps = {
  field: IApplyField;
  value: FieldValue;
  invalid: boolean;
  copy: (typeof COPY)["ru"];
  onChange: (value: FieldValue) => void;
};

function FormControl({ field, value, invalid, copy, onChange }: FormControlProps) {
  const [draft, setDraft] = useState("");

  if (field.type === "base64") {
    const file = value instanceof File ? value : undefined;
    return (
      <label className={classNames(styles.fileInput, { [styles.invalid]: invalid })}>
        <input id={field.name} type="file" accept="application/pdf,.pdf" onChange={(event) => onChange(event.target.files?.[0])} />
        <FileEarmarkPdf />
        <span>{file ? file.name : copy.chooseFile}</span>
        {file && (
          <button
            type="button"
            className={styles.clearButton}
            aria-label="Remove file"
            onClick={(event) => {
              event.preventDefault();
              onChange(undefined);
            }}
          >
            <X />
          </button>
        )}
      </label>
    );
  }

  if (field.type === "tags") {
    const tags = Array.isArray(value) ? value : [];
    const commit = () => {
      const next = draft.trim();
      if (next && !tags.includes(next)) onChange([...tags, next]);
      setDraft("");
    };
    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" || event.key === ",") {
        event.preventDefault();
        commit();
      } else if (event.key === "Backspace" && !draft && tags.length) {
        onChange(tags.slice(0, -1));
      }
    };

    return (
      <div className={classNames(styles.tagsInput, { [styles.invalid]: invalid })}>
        {tags.map((tag) => (
          <span key={tag} className={styles.tag}>
            {tag}
            <button type="button" aria-label={`Remove ${tag}`} onClick={() => onChange(tags.filter((item) => item !== tag))}>
              <X />
            </button>
          </span>
        ))}
        <input
          id={field.name}
          value={draft}
          placeholder={tags.length ? "" : copy.tagsPlaceholder}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commit}
        />
      </div>
    );
  }

  return (
    <input
      id={field.name}
      className={classNames(styles.input, { [styles.invalid]: invalid })}
      type={field.type}
      required={field.required}
      autoComplete={AUTOCOMPLETE[field.name]}
      value={typeof value === "string" ? value : ""}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

// Short inputs sit two per row; these take the full form width.
const WIDE_FIELD_TYPES = ["base64", "tags"];

const AUTOCOMPLETE: Record<string, string> = {
  firstName: "given-name",
  lastName: "family-name",
  userEmail: "email",
  userPhone: "tel",
};

interface IVacancyResponse {
  vacancy: IVacancyDetails;
  apply?: IApply;
}

interface IVacancyDetails {
  name: string;
  location: string;
  salary: string;
  employmentType: string;
  workRegime: string;
  workFormat: string;
  description: string;
  responsibilities: string;
  requirements: string;
  terms: string;
  weOffer: string;
  descriptionLabel: string;
  responsibilitiesLabel: string;
  requirementsLabel: string;
  termsLabel: string;
  weOfferLabel: string;
  vacancyLink?: string;
}

interface IApply {
  form: IApplyField[];
}

interface IApplyField {
  name: string;
  type: "text" | "email" | "tel" | "base64" | "tags" | string;
  label: string;
  hint?: string;
  required: boolean;
}
